/**
 * The application's last line of defence against a render-time exception.
 *
 * React 18 unmounts the entire tree when a render throws and nothing catches
 * it. With no boundary anywhere in this app, any such throw — in a page, a
 * dialog, a cell renderer — left the user staring at a blank white screen with
 * no message, no way back, and nothing in the UI to report. That is what the
 * "Manage Consolidation Rules screen goes blank" reports were: not the modal
 * closing, the whole app unmounting underneath it.
 *
 * This catches the throw, keeps the error, and shows something the user can act
 * on. The details block matters as much as the recovery: a screenshot of this
 * panel names the failing component, which a blank page never did.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isChunkLoadError, reloadOnceForChunkError } from '@/utils/chunkReload';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  componentStack: string;
}

export default class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: '' };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // A stale tab asking for a chunk the last deploy removed: a reload fixes it,
    // so do that rather than show the panel. The second time in a row it shows.
    if (isChunkLoadError(error) && reloadOnceForChunkError()) return;
    this.setState({ componentStack: info.componentStack ?? '' });
    // Keep it in the console too: the browser's own overlay is gone by the time
    // this renders, and this is often the only copy of the stack.
    console.error('[ConsolidateAI] Unhandled render error', error, info.componentStack);
  }

  private reset = () => {
    this.setState({ error: null, componentStack: '' });
  };

  render() {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
        <div className="w-full max-w-2xl rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
          <h1 className="text-xl font-semibold text-gray-900">Something broke on this screen</h1>
          <p className="mt-2 text-sm leading-relaxed text-gray-600">
            The page stopped rather than showing you numbers it could not render. Nothing
            you had saved is affected — unsaved edits on this screen are lost.
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            {/* Re-render the same tree first: a transient failure (a half-loaded
                response, a stale reference) clears without a full reload and
                keeps the rest of the session. */}
            <button
              type="button"
              onClick={this.reset}
              className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-50"
            >
              Reload the app
            </button>
          </div>

          {/* Collapsed by default — it is for the screenshot that comes back
              with the bug report, not for the person trying to get on with the
              close. */}
          <details className="mt-6">
            <summary className="cursor-pointer text-xs font-medium uppercase tracking-wider text-gray-400 hover:text-gray-600">
              Technical details
            </summary>
            <pre className="mt-3 max-h-64 overflow-auto rounded-lg bg-gray-900 p-4 text-[11px] leading-relaxed text-gray-100">
              {error.message}
              {error.stack ? `\n\n${error.stack}` : ''}
              {componentStack ? `\n\nComponent stack:${componentStack}` : ''}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
